import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { getEmailPreferences, updateEmailPreferences } from '../utils/api';

export interface PriceAlertSettings {
  isEnabled: boolean;
  threshold: number;
}

const PRICE_ALERT_STORAGE_KEY = 'price_alert_settings';

const defaultSettings: PriceAlertSettings = {
  isEnabled: true,
  threshold: 10
};

export const usePriceAlert = () => {
  const [priceAlertSettings, setPriceAlertSettings] = useState<PriceAlertSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(false);
  const [syncTrigger, setSyncTrigger] = useState(0);

  // Load settings from backend (if authenticated) or localStorage on mount
  useEffect(() => {
    const loadSettings = async () => {
      const authToken = localStorage.getItem('auth_token');

      if (authToken) {
        // User is authenticated, fetch from backend
        try {
          const response = await getEmailPreferences();
          if (response.success && response.data?.emailPreferences) {
            const backendSettings = {
              isEnabled: response.data.emailPreferences.priceAlertEnabled ?? defaultSettings.isEnabled,
              threshold: response.data.emailPreferences.priceDropThreshold ?? defaultSettings.threshold
            };
            setPriceAlertSettings(backendSettings);
            // Sync to localStorage
            localStorage.setItem(PRICE_ALERT_STORAGE_KEY, JSON.stringify(backendSettings));
          }
        } catch (error) {
          console.error('Error loading price alert settings from backend:', error);
          // Fall back to localStorage
          loadFromLocalStorage();
        }
      } else {
        // Not authenticated, use localStorage
        loadFromLocalStorage();
      }
    };

    const loadFromLocalStorage = () => {
      try {
        const savedSettings = localStorage.getItem(PRICE_ALERT_STORAGE_KEY);
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          setPriceAlertSettings({
            isEnabled: parsed.isEnabled ?? defaultSettings.isEnabled,
            threshold: parsed.threshold ?? defaultSettings.threshold
          });
        }
      } catch (error) {
        console.error('Error loading price alert settings from localStorage:', error);
      }
    };

    loadSettings();

    // Listen for sync events from other components
    const handleSync = () => {
      loadSettings();
    };
    window.addEventListener('priceAlertSync', handleSync);
    return () => window.removeEventListener('priceAlertSync', handleSync);
  }, [syncTrigger]);

  // Save settings to backend (if authenticated) and localStorage
  const updateSettings = async (updates: Partial<PriceAlertSettings>) => {
    const newSettings = { ...priceAlertSettings, ...updates };
    setPriceAlertSettings(newSettings);

    // Save to localStorage immediately
    try {
      localStorage.setItem(PRICE_ALERT_STORAGE_KEY, JSON.stringify(newSettings));
    } catch (error) {
      console.error('Error saving price alert settings to localStorage:', error);
    }

    // If authenticated, also save to backend
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      setIsLoading(true);
      try {
        await updateEmailPreferences(newSettings.isEnabled, newSettings.threshold);
        // Trigger sync event to notify other components
        window.dispatchEvent(new Event('priceAlertSync'));
      } catch (error) {
        console.error('Error saving price alert settings to backend:', error);
        toast.error('Failed to save settings to server');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Even without backend, trigger sync for localStorage updates
      window.dispatchEvent(new Event('priceAlertSync'));
    }
  };

  const handleToggleGlobalAlert = async () => {
    const newState = !priceAlertSettings.isEnabled;
    await updateSettings({ isEnabled: newState });

    if (newState) {
      toast.success(
        `Price alerts enabled! We'll send email notifications when your favorite shoes drop ${priceAlertSettings.threshold}% or more.`,
        { duration: 5000 }
      );
    } else {
      toast('Price alerts disabled - No more email notifications', { duration: 3000 });
    }
  };

  const handleThresholdChange = async (threshold: number) => {
    await updateSettings({ threshold });
    toast.success(`Email alert threshold updated to ${threshold}% - We'll notify you when prices drop by ${threshold}% or more`);
  };

  return {
    priceAlertSettings,
    handleToggleGlobalAlert,
    handleThresholdChange,
    updateSettings,
    isLoading
  };
};